import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  TeButton,
  TeContainer,
  TeContainerSlim,
  TeLinearLoader,
  TeModal,
  TeSlider,
  TeText,
} from '@topcon/tuicl-react';
import { FnLimiter } from '@topcon/tuicl/dist/common/utils';
import { TeSliderChangeEvent } from '@topcon/tuicl';
import ReactCrop, { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import { common } from '../../providers/theme/theme.index';
import { createCroppedLogoBlob } from './AdjustLogoModal.utilities';

interface Props {
  logo?: { url: string; name: string };
  pending?: boolean;
  title?: string;
  onClose: () => void;
  onSubmit?: (blob: Blob) => void;
}

const MODAL_WIDTH = 480;
const IMG_CONTAINER_HEIGHT = 384;
const IMG_CONTAINER_WIDTH = IMG_CONTAINER_HEIGHT;
const ZOOM_AREA_HEIGHT = 126;
const ZOOM_AREA_WIDTH = IMG_CONTAINER_WIDTH - 16 * 2;
const BODY_HEIGHT = IMG_CONTAINER_HEIGHT + ZOOM_AREA_HEIGHT + 8 + 16 * 2;
const MIN_ZOOM = 100;
const MAX_ZOOM = 200;
const ZOOM_STEP = 10;

const limiter = new FnLimiter();

const DEFAULT_CROP = {
  unit: 'px',
  x: 0,
  y: 0,
  width: IMG_CONTAINER_WIDTH,
  height: IMG_CONTAINER_HEIGHT,
} as const;

const AdjustLogoModal = ({ logo, onClose, onSubmit, pending, title }: Props) => {
  const { t } = useTranslation('settings');
  const [crop, setCrop] = useState<Crop>(DEFAULT_CROP);
  const [zoomPct, setZoomPct] = useState(MIN_ZOOM);
  const [imgZoom, setImgZoom] = useState(MIN_ZOOM);
  const [imgUrl, setImgUrl] = useState<string>('');
  const open = !!logo;

  useEffect(() => {
    if (open) {
      setCrop(DEFAULT_CROP);
      setImgUrl(logo?.url ?? '');
      setZoomPct(MIN_ZOOM);
      setImgZoom(MIN_ZOOM);
    } else {
      setImgUrl('');
    }
  }, [open, logo]);

  const onZoom = (e: TeSliderChangeEvent) => {
    const { value } = e;
    if (typeof value !== 'number') return;
    setZoomPct(value);
  };

  useEffect(() => {
    limiter.debounce(() => setImgZoom(zoomPct), 200);
  }, [zoomPct]);

  useEffect(() => {
    if (!open) setZoomPct(MIN_ZOOM);
  }, [open]);

  const handleSubmit = async () => {
    try {
      const blob = await createCroppedLogoBlob(crop, imgUrl, {
        imgZoom,
        containerHeight: IMG_CONTAINER_HEIGHT,
      });
      onSubmit?.(blob);
    } catch (error) {
      // TODO: Handle error appropriately (e.g., show a toast notification)
      console.error('Failed to crop image:', error);
    }
  };

  const isPending = pending;

  return (
    <TeModal
      variant="dark"
      open={open}
      closeMode="button"
      teclose={onClose}
      modalTitle={title ?? t('generalInformation.newImage.adjustAndSave')}
      contentPadding={0}
      closeable
    >
      <TeContainer
        backgroundColor="background.primary"
        gap="xs"
        width={MODAL_WIDTH}
        height={BODY_HEIGHT}
        padding={['m', 's', 'm', 's']}
        alignItems="center"
        column
        grow
      >
        <TeContainer
          width={IMG_CONTAINER_WIDTH}
          height={IMG_CONTAINER_HEIGHT}
          padding={0}
          justifyContent="center"
          alignItems="center"
          column
          grow
          relative
        >
          <ReactCrop
            aspect={1}
            crop={crop}
            onChange={setCrop}
            circularCrop
            // disabled
            // keepSelection
            // locked
          >
            <ImageForeground
              $backgroundSize={`${imgZoom}%`}
              $backgroundImage={`url('${imgUrl}')`}
            />
          </ReactCrop>
        </TeContainer>
        <TeContainer
          gap={24}
          width={IMG_CONTAINER_WIDTH}
          height={ZOOM_AREA_HEIGHT}
          padding={['m', 'm', 'l', 'm']}
          borderColor="border.secondary"
          borderRadius={common.borderRadius.values.m}
          column
          grow
        >
          {pending ? (
            <TeContainerSlim column grow justifyContent="center" alignItems="center">
              <TeText size={14} weight={500} color="text.primary">
                {logo?.name}
              </TeText>
              <TeContainerSlim height={16} />
              <TeLinearLoader />
              <TeContainerSlim height={8} />
              <TeText size={12} weight={400} italic color="text.secondary">
                {t('generalInformation.newImage.pendingText')}
              </TeText>
            </TeContainerSlim>
          ) : (
            <>
              <TeContainerSlim column grow gap={4}>
                <TeText size={14} weight={700} color="text.primary">
                  {t('generalInformation.newImage.zoom')}
                </TeText>
                <TeText size={12} weight={400} color="text.secondary">
                  {t('generalInformation.newImage.useTheSlider')}
                </TeText>
              </TeContainerSlim>
              <TeContainerSlim width={ZOOM_AREA_WIDTH} gap="xs" alignItems="center">
                <TeButton
                  variant="neutral"
                  icon="remove"
                  size="xs"
                  disabled={zoomPct <= MIN_ZOOM}
                  teclick={() => setZoomPct(Math.max(MIN_ZOOM, zoomPct - ZOOM_STEP))}
                />
                <TeSlider min={MIN_ZOOM} max={MAX_ZOOM} value={zoomPct} techange={onZoom} />
                <TeButton
                  variant="neutral"
                  icon="add"
                  size="xs"
                  disabled={zoomPct >= MAX_ZOOM}
                  teclick={() => setZoomPct(Math.min(MAX_ZOOM, zoomPct + ZOOM_STEP))}
                />
              </TeContainerSlim>
            </>
          )}
        </TeContainer>
      </TeContainer>

      <TeButton slot="tertiary" text={t('cancel')} variant="neutral" teclick={onClose} size="s" />
      <TeButton
        slot="primary"
        text={t('generalInformation.newImage.saveImage')}
        variant="primary"
        teclick={handleSubmit}
        size="s"
        disabled={isPending}
        loading={isPending}
      />
    </TeModal>
  );
};

const ImageForeground = styled.div<{ $backgroundSize: string; $backgroundImage: string }>`
  width: ${IMG_CONTAINER_HEIGHT}px;
  height: ${IMG_CONTAINER_HEIGHT}px;
  background-size: ${(props) => props.$backgroundSize};
  background-position: center;
  background-repeat: no-repeat;
  background-image: ${(props) => props.$backgroundImage};
`;

export default AdjustLogoModal;
